import { useCallback, useMemo, useReducer } from "react";

/**
 * Výběr fotek na stránce Média (řez 03, Kontrakt bod 6).
 *
 * Pravidlo kliku (drží ho mřížka): prostý klik na dlaždici otevře prohlížeč; zaškrtávátko
 * a Cmd/Ctrl-klik `toggle`; Shift-klik `extendTo`.
 *
 * Akce `toggle`, `extendTo`, `selectAll` a `clear` mají stabilní identitu po celou dobu
 * života háku; `clear` nad prázdným výběrem stav nemění. Objekt `MediaSelection` se mění
 * s každou změnou výběru, proto ho efekt nikdy nemá v závislostech: rodič destrukturuje
 * `const { clear } = selection` a efekt nulování výběru závisí na `[…filtry, clear]`.
 */
export interface MediaSelection {
  selected: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  /** Zaškrtávátko, Cmd/Ctrl-klik. */
  toggle: (id: string) => void;
  /** Shift-klik: rozsah od kotvy. */
  extendTo: (id: string, orderedIds: readonly string[]) => void;
  /** „Vybrat vše načtené“. */
  selectAll: (ids: readonly string[]) => void;
  clear: () => void;
}

/**
 * `anchor` = poslední PŘIDANÁ dlaždice (Shift vybírá „od poslední vybrané“); odebráním
 * přestane být kotvou a další Shift-klik vybere jen klepnutou.
 */
export interface SelectionState {
  selected: ReadonlySet<string>;
  anchor: string | null;
}

export type SelectionEvent =
  | { type: "toggle"; id: string }
  | { type: "extendTo"; id: string; orderedIds: readonly string[] }
  | { type: "selectAll"; ids: readonly string[] }
  | { type: "clear" };

export const EMPTY_SELECTION: SelectionState = Object.freeze({
  selected: new Set<string>(),
  anchor: null,
});

function withAdded(state: SelectionState, ids: Iterable<string>): Set<string> {
  const next = new Set(state.selected);
  for (const id of ids) next.add(id);
  return next;
}

/**
 * Čistý přechod výběru. Každá změna vrací novou `Set` (React porovnává identitou),
 * `state.selected` se nikdy nemutuje; `clear` nad prázdným výběrem vrátí týž stav, aby
 * nulování v efektu rodiče nevyvolalo překreslení.
 */
export function selectionReducer(state: SelectionState, event: SelectionEvent): SelectionState {
  switch (event.type) {
    case "toggle": {
      if (state.selected.has(event.id)) {
        const next = new Set(state.selected);
        next.delete(event.id);
        return { selected: next, anchor: state.anchor === event.id ? null : state.anchor };
      }
      return { selected: withAdded(state, [event.id]), anchor: event.id };
    }
    case "extendTo": {
      const from = state.anchor === null ? -1 : event.orderedIds.indexOf(state.anchor);
      const to = event.orderedIds.indexOf(event.id);
      if (from === -1 || to === -1) {
        // Bez kotvy, nebo kotva / klepnutá mimo načtené pořadí (seznam se mezitím změnil):
        // vybere jen klepnutou a ta se stane kotvou.
        return { selected: withAdded(state, [event.id]), anchor: event.id };
      }
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      return {
        selected: withAdded(state, event.orderedIds.slice(lo, hi + 1)),
        anchor: state.anchor,
      };
    }
    case "selectAll":
      return { selected: new Set(event.ids), anchor: null };
    case "clear":
      if (state.selected.size === 0 && state.anchor === null) return state;
      return { selected: new Set(), anchor: null };
  }
}

export function useMediaSelection(): MediaSelection {
  const [state, dispatch] = useReducer(selectionReducer, EMPTY_SELECTION);

  const toggle = useCallback((id: string) => dispatch({ type: "toggle", id }), []);
  const extendTo = useCallback(
    (id: string, orderedIds: readonly string[]) => dispatch({ type: "extendTo", id, orderedIds }),
    [],
  );
  const selectAll = useCallback(
    (ids: readonly string[]) => dispatch({ type: "selectAll", ids }),
    [],
  );
  const clear = useCallback(() => dispatch({ type: "clear" }), []);

  return useMemo(
    () => ({
      selected: state.selected,
      isSelected: (id: string) => state.selected.has(id),
      toggle,
      extendTo,
      selectAll,
      clear,
    }),
    [state, toggle, extendTo, selectAll, clear],
  );
}
